import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { PRIVATE_TOP_PATH } from '@/constants/routes';

export type ProfileSettingsOverlayState = {
  entryMode: 'history' | 'direct';
  emailChangeSuccess: boolean;
};

const PROFILE_SETTINGS_OVERLAY_KEY = 'profileSettingsOverlay';

const getObjectState = (state: unknown): Record<string, unknown> =>
  state && typeof state === 'object'
    ? { ...(state as Record<string, unknown>) }
    : {};

export const getProfileSettingsOverlayState = (
  state: unknown
): ProfileSettingsOverlayState | null => {
  if (!state || typeof state !== 'object') return null;
  const candidate = (state as Record<string, unknown>)[
    PROFILE_SETTINGS_OVERLAY_KEY
  ];
  if (!candidate || typeof candidate !== 'object') return null;
  const value = candidate as Record<string, unknown>;
  if (value.entryMode !== 'history' && value.entryMode !== 'direct')
    return null;
  return {
    entryMode: value.entryMode,
    emailChangeSuccess: value.emailChangeSuccess === true,
  };
};

export const useProfileSettingsOverlayNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const overlay = getProfileSettingsOverlayState(location.state);

  const openProfileSettings = useCallback(
    async (options?: {
      emailChangeSuccess?: boolean;
      entryMode?: 'history' | 'direct';
    }) => {
      const entryMode = options?.entryMode ?? 'history';
      const currentState = getObjectState(location.state);
      const state = {
        ...currentState,
        [PROFILE_SETTINGS_OVERLAY_KEY]: {
          entryMode,
          emailChangeSuccess: options?.emailChangeSuccess === true,
        },
      };

      if (entryMode === 'direct') {
        delete currentState[PROFILE_SETTINGS_OVERLAY_KEY];
        await navigate(PRIVATE_TOP_PATH, {
          replace: true,
          state: currentState,
        });
        return navigate(PRIVATE_TOP_PATH, {
          preventScrollReset: true,
          state,
        });
      }

      return navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        { preventScrollReset: true, state }
      );
    },
    [
      location.hash,
      location.pathname,
      location.search,
      location.state,
      navigate,
    ]
  );

  const closeProfileSettings = useCallback(() => {
    if (!overlay) return;

    if (overlay.entryMode === 'history') {
      return navigate(-1);
    }

    const state = getObjectState(location.state);
    delete state[PROFILE_SETTINGS_OVERLAY_KEY];
    return navigate(PRIVATE_TOP_PATH, { replace: true, state });
  }, [location.state, navigate, overlay]);

  return { overlay, openProfileSettings, closeProfileSettings };
};
